import React from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { toastError, toastSuccess } from '../../../../shared/toastHelper';
import { AddPaymentMethodForm, PaymentMethodCard, buildStripeAppearance } from '../PaymentMethods';
import useTheme from '../../hooks/useTheme';
import formatPledgeAmount from '../../../shared/utils/formatPledgeAmount';
import { Alert, Button } from '@opendate-app/setlist';

export default function PledgeCardSection({ teamId, pledge, onPledgeUpdated }) {
  const [setup, setSetup] = React.useState(null);
  const [starting, setStarting] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);
  const [unsavedPaymentMethodId, setUnsavedPaymentMethodId] = React.useState(null);
  const [retryPrompt, setRetryPrompt] = React.useState(null);
  const [retrying, setRetrying] = React.useState(false);
  const url = `/teams/${teamId}/crm/pledges/${pledge.id}/payment_method.json`;
  const card = pledge.card;
  const { resolvedTheme } = useTheme();
  const inThemedPage = document.body.classList.contains('od-themed');
  const appearance = buildStripeAppearance(inThemedPage ? resolvedTheme : 'light');

  const startSetup = async () => {
    setRetryPrompt(null);
    setStarting(true);
    try {
      const { data } = await axios.post(url);
      setSetup({
        clientSecret: data.client_secret,
        stripePromise: loadStripe(data.stripe_publishable_key, { stripeAccount: data.stripe_connected_account_id }),
      });
    } catch (e) {
      toastError(e?.response?.data?.error || 'Could not start card entry. Try again.');
    } finally {
      setStarting(false);
    }
  };

  const saveCard = async (paymentMethodId) => {
    setSetup(null);
    try {
      const { data } = await axios.patch(url, { payment_method_id: paymentMethodId });
      setUnsavedPaymentMethodId(null);
      onPledgeUpdated(data);
      const failed = (data.installments || []).filter((installment) => installment.derived_status === 'failed');
      if (failed.length > 0) {
        setRetryPrompt(failed.map((installment) => ({
          id: installment.id,
          sequence: installment.sequence,
          cents: installment.charged_amount_cents || installment.amount_cents,
        })));
      }
    } catch (e) {
      setUnsavedPaymentMethodId(paymentMethodId);
      toastError(e?.response?.data?.error || 'Could not save the card. Try again.');
    }
  };

  const retryFailedPayments = async () => {
    setRetrying(true);
    const retried = new Map();
    let lastError = null;
    for (const { id: installmentId } of retryPrompt) {
      try {
        const { data } = await axios.post(
          `/teams/${teamId}/crm/pledges/${pledge.id}/installments/${installmentId}/retry_charge.json`
        );
        retried.set(installmentId, data);
      } catch (e) {
        lastError = e;
      }
    }
    if (lastError) {
      toastError(`Retried ${retried.size} of ${retryPrompt.length} failed payments. ${lastError?.response?.data?.error || 'Try the rest again from the installment schedule.'}`);
    } else {
      toastSuccess(retried.size === 1 ? 'Retrying the failed payment.' : `Retrying ${retried.size} failed payments.`);
    }
    onPledgeUpdated({
      ...pledge,
      installments: (pledge.installments || []).map((installment) => (
        retried.has(installment.id) ? { ...installment, ...retried.get(installment.id) } : installment
      )),
    });
    setRetryPrompt(null);
    setRetrying(false);
  };

  const removeCard = async () => {
    setRemoving(true);
    try {
      const { data } = await axios.delete(url);
      setRetryPrompt(null);
      onPledgeUpdated(data);
    } catch (e) {
      toastError('Could not remove the card. Try again.');
    } finally {
      setRemoving(false);
    }
  };

  if (setup) {
    return (
      <Elements stripe={setup.stripePromise} options={{ clientSecret: setup.clientSecret, appearance }}>
        <AddPaymentMethodForm submitLabel="Save card" onSuccess={saveCard} onCancel={() => setSetup(null)} />
      </Elements>
    );
  }

  return (
    <div>
      {retryPrompt && (
        <Alert
          color="warning"
          role="status"
          title={`${retryPrompt.length} failed ${retryPrompt.length === 1 ? 'payment' : 'payments'} can be retried with this card.`}
          description={retryPrompt
            .map((installment) => `Installment ${installment.sequence}: ${formatPledgeAmount(installment.cents, { withDecimals: true })}`)
            .join(' · ')}
          actions={(
            <>
              <Button variant="secondary" size="sm" disabled={retrying} onClick={() => setRetryPrompt(null)}>Not now</Button>
              <Button variant="primary" size="sm" disabled={retrying} onClick={retryFailedPayments}>Retry now</Button>
            </>
          )}
        />
      )}
      {card && (
        <PaymentMethodCard
          paymentMethod={{ brand: card.brand, last4: card.last4, expMonth: card.exp_month, expYear: card.exp_year }}
          onDelete={retrying ? undefined : removeCard}
          deleting={removing}
        />
      )}
      {unsavedPaymentMethodId ? (
        <button type="button" className="btn btn-setlist-secondary" onClick={() => saveCard(unsavedPaymentMethodId)}>
          Retry saving card
        </button>
      ) : (
        <button type="button" className="btn btn-setlist-secondary" disabled={starting} onClick={startSetup}>
          {card ? 'Replace card' : 'Add card'}
        </button>
      )}
    </div>
  );
}
